import React from "react";
import ChatbotButton from "./components/chatbot-button";
import Chat from "./components/chat";
import { ChatLogProvider } from "../contexts/chat-log";
import { ChatbotConfigProvider, useChatbotConfig } from "../contexts/chatbot-config";
import { BottomSheetProvider } from "../contexts/bottom-sheet";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import styles from "./styles.module.scss";
import {
  clearSessionId,
  setupLinkTracking,
} from "../libs/fns";
import { applyFontToElement, type FontKey } from "../libs/webfont";

gsap.registerPlugin(useGSAP);

function AppContent({ open, setOpen }: { open: boolean; setOpen: React.Dispatch<React.SetStateAction<boolean>> }) {
  const { chatbotData, isBotBlocked } = useChatbotConfig();
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (chatbotData?.set_font && wrapperRef.current) {
      applyFontToElement(wrapperRef.current, chatbotData.set_font as FontKey);
    }
  }, [chatbotData?.set_font]);

  // サービス解約済みまたはチャットボット停止中の場合はウィジェット全体を非表示
  if (isBotBlocked) {
    return null;
  }

  return (
    <div ref={wrapperRef} className={styles.wrapper} style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '14px',
      lineHeight: '1.5'
    }}>
      <BottomSheetProvider>
        <ChatLogProvider>
          <Chat open={open} setOpen={setOpen} />
          <ChatbotButton open={open} setOpen={setOpen} />
        </ChatLogProvider>
      </BottomSheetProvider>
    </div>
  );
}

function App() {
  const [open, setOpen] = React.useState<boolean>(false);
  const [isSessionReady, setIsSessionReady] = React.useState<boolean>(false);
  const isInitializedRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    // 初回マウント時のみ実行（コンポーネント再マウント時は実行しない）
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    // ブラウザリフレッシュ時に既存セッションをクリア
    clearSessionId();

    // セッションはinitializeChat関数内で自動的に作成される
    setIsSessionReady(true);

    // iframe内でのリンクトラッキング設定
    // DOM変更を監視してリンクトラッキングを適用
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              setupLinkTracking(node as HTMLElement);
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // 初期リンクトラッキング設定
    setupLinkTracking();

    // 親ウィンドウにレディ状態を通知
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'IFRAME_APP_READY',
        source: 'onetouch-ai-widget'
      }, '*');
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {isSessionReady && (
        <ChatbotConfigProvider>
          <AppContent open={open} setOpen={setOpen} />
        </ChatbotConfigProvider>
      )}
    </>
  );
}

export default App;
